import React, { useMemo, useState } from 'react';
import {
  Download,
  Globe,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Search,
} from 'lucide-react';
import axios from 'axios';

interface List {
  listid: number;
  name?: string;
  listname?: string;
}

interface Segment {
  segmentid: number;
  segmentname: string;
}

interface Props {
  lists: List[];
  segments: Segment[];
}

type Scope = 'all' | 'segment' | 'list' | 'domain';

interface FilterRow {
  filter: string;
  domains: number;
  contacts: number;
  pct: number;
  avgConfidence?: number;
}

interface MxSignal {
  kind: string;
  vendor: string;
  detail: string;
}

interface DomainRow {
  domain: string;
  contacts: number;
  mxHosts: string[];
  mxIps?: string[];
  ptrHosts?: string[];
  spfVendors?: string[];
  dkimVendors?: string[];
  txtHits?: string[];
  signals?: MxSignal[];
  filter: string;
  confidence?: number;
  lookedUpAt: string | null;
}

function listLabel(l: List): string {
  return l.name ?? l.listname ?? `List ${l.listid}`;
}

export default function MxLookup({ lists, segments }: Props) {
  const [scope, setScope] = useState<Scope>('all');
  const [segmentId, setSegmentId] = useState<number | ''>('');
  const [segmentSearch, setSegmentSearch] = useState('');
  const [listId, setListId] = useState<number | ''>('');
  const [domainInput, setDomainInput] = useState('');
  const [excludeEdu, setExcludeEdu] = useState(true);

  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{
    processed: number;
    total: number;
    remaining: number;
  } | null>(null);
  const [summary, setSummary] = useState<{
    scopeLabel: string;
    totalContacts: number;
    totalDomains: number;
    scannedDomains: number;
    filters: FilterRow[];
  } | null>(null);

  const [domainResult, setDomainResult] = useState<DomainRow | null>(null);

  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [detailPage, setDetailPage] = useState(1);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailRows, setDetailRows] = useState<DomainRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const pageSize = 50;

  const filteredSegments = useMemo(() => {
    const q = segmentSearch.trim().toLowerCase();
    if (!q) return segments;
    return segments.filter(
      (s) =>
        s.segmentname.toLowerCase().includes(q) ||
        String(s.segmentid).includes(q)
    );
  }, [segments, segmentSearch]);

  const sortedLists = useMemo(
    () => [...lists].sort((a, b) => listLabel(a).localeCompare(listLabel(b))),
    [lists]
  );

  const canRun =
    scope === 'all' ||
    (scope === 'segment' && segmentId !== '') ||
    (scope === 'list' && listId !== '') ||
    (scope === 'domain' && domainInput.trim().includes('.'));

  const scopeBody = () => {
    if (scope === 'segment') return { scope, segmentId, excludeEdu };
    if (scope === 'list') return { scope, listId, excludeEdu };
    if (scope === 'domain') return { scope, domain: domainInput.trim(), excludeEdu: false };
    return { scope: 'all' as const, excludeEdu };
  };

  const scopeQuery = () => {
    const params = new URLSearchParams({ scope });
    if (scope === 'segment' && segmentId !== '') params.set('segmentId', String(segmentId));
    if (scope === 'list' && listId !== '') params.set('listId', String(listId));
    if (scope === 'domain' && domainInput.trim()) params.set('domain', domainInput.trim());
    params.set('excludeEdu', excludeEdu ? '1' : '0');
    return params;
  };

  const resetResults = () => {
    setSummary(null);
    setProgress(null);
    setSelectedFilter(null);
    setDetailRows([]);
    setDetailTotal(0);
    setDomainResult(null);
    setError(null);
  };

  const loadSummary = async () => {
    const res = await axios.get<{
      scopeLabel: string;
      totalContacts: number;
      totalDomains: number;
      scannedDomains: number;
      filters: FilterRow[];
    }>(`/api/interspire/utilities/mx-lookup/summary?${scopeQuery()}`, { timeout: 120_000 });
    setSummary(res.data);
  };

  const loadFilterPage = async (filter: string, page: number) => {
    setDetailLoading(true);
    try {
      const params = scopeQuery();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const res = await axios.get<{
        total: number;
        page: number;
        rows: DomainRow[];
      }>(
        `/api/interspire/utilities/mx-lookup/filters/${encodeURIComponent(filter)}/domains?${params}`,
        { timeout: 120_000 }
      );
      setDetailRows(res.data.rows);
      setDetailTotal(res.data.total);
      setDetailPage(res.data.page);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load domains');
    } finally {
      setDetailLoading(false);
    }
  };

  const applyExcludeEdu = async (next: boolean) => {
    setExcludeEdu(next);
    if (scope === 'domain') return;
    if (!summary && !selectedFilter) return;
    setError(null);
    try {
      const params = new URLSearchParams({ scope });
      if (scope === 'segment' && segmentId !== '') params.set('segmentId', String(segmentId));
      if (scope === 'list' && listId !== '') params.set('listId', String(listId));
      params.set('excludeEdu', next ? '1' : '0');
      const res = await axios.get<{
        scopeLabel: string;
        totalContacts: number;
        totalDomains: number;
        scannedDomains: number;
        filters: FilterRow[];
      }>(`/api/interspire/utilities/mx-lookup/summary?${params}`, { timeout: 120_000 });
      setSummary(res.data);
      if (selectedFilter) {
        params.set('page', '1');
        params.set('pageSize', String(pageSize));
        setDetailLoading(true);
        const detail = await axios.get<{
          total: number;
          page: number;
          rows: DomainRow[];
        }>(
          `/api/interspire/utilities/mx-lookup/filters/${encodeURIComponent(selectedFilter)}/domains?${params}`,
          { timeout: 120_000 }
        );
        setDetailRows(detail.data.rows);
        setDetailTotal(detail.data.total);
        setDetailPage(detail.data.page);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to update summary');
    } finally {
      setDetailLoading(false);
    }
  };

  const runDomainLookup = async (forceRefresh: boolean) => {
    const domain = domainInput.trim();
    if (!domain.includes('.')) {
      setError('Enter a valid domain (e.g. example.com).');
      return;
    }
    setRunning(true);
    setError(null);
    setSummary(null);
    setSelectedFilter(null);
    try {
      const res = await axios.post<DomainRow>('/api/interspire/utilities/mx-lookup/domain', {
        domain,
        forceRefresh,
      }, { timeout: 60_000 });
      setDomainResult(res.data);
    } catch (e: any) {
      setDomainResult(null);
      setError(e?.response?.data?.error ?? e?.message ?? 'Domain lookup failed');
    } finally {
      setRunning(false);
    }
  };

  const runScan = async (forceRefresh: boolean) => {
    if (scope === 'domain') {
      await runDomainLookup(forceRefresh);
      return;
    }
    if (!canRun) {
      setError(
        scope === 'segment'
          ? 'Select a segment first.'
          : scope === 'list'
            ? 'Select a list first.'
            : 'Invalid scope.'
      );
      return;
    }
    setRunning(true);
    setError(null);
    setSelectedFilter(null);
    setDetailRows([]);
    setDomainResult(null);
    try {
      await axios.post(
        '/api/interspire/utilities/mx-lookup/inventory',
        {
          ...scopeBody(),
          forceRefresh: true,
        },
        { timeout: 180_000 }
      );

      let done = false;
      let first = true;
      while (!done) {
        const res = await axios.post<{
          done: boolean;
          processed: number;
          remaining: number;
          total: number;
        }>(
          '/api/interspire/utilities/mx-lookup/scan',
          {
            ...scopeBody(),
            forceRefresh: forceRefresh && first,
            batchSize: 20,
          },
          { timeout: 180_000 }
        );
        first = false;
        setProgress({
          processed: res.data.processed,
          total: res.data.total,
          remaining: res.data.remaining,
        });
        done = res.data.done;
      }
      await loadSummary();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'MX lookup failed');
    } finally {
      setRunning(false);
    }
  };

  const openFilter = async (filter: string) => {
    setSelectedFilter(filter);
    setDetailPage(1);
    await loadFilterPage(filter, 1);
  };

  const exportFilter = async () => {
    if (!selectedFilter) return;
    setExporting(true);
    try {
      const res = await axios.get(
        `/api/interspire/utilities/mx-lookup/filters/${encodeURIComponent(selectedFilter)}/export?${scopeQuery()}`,
        { responseType: 'blob', timeout: 0 }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mx_lookup_${selectedFilter.replace(/[^a-z0-9._-]+/gi, '_')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch {
      window.alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const detailPages = Math.max(1, Math.ceil(detailTotal / pageSize));
  const pctComplete =
    progress && progress.total > 0
      ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
      : 0;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-5 space-y-5">
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Globe className="h-4 w-4 text-sky-500" />
          Mail Filter Detection
        </h3>
        <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
          Multi-signal detection (MX, SPF, DKIM, TXT, PTR, IP ranges) with confidence scores.
          Catches Microsoft + third-party connector cases MX-only misses. Results stay cached until Refresh.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(
          [
            { id: 'all' as const, label: 'All active' },
            { id: 'segment' as const, label: 'Segment' },
            { id: 'list' as const, label: 'List' },
            { id: 'domain' as const, label: 'Domain' },
          ] as const
        ).map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => {
              setScope(opt.id);
              resetResults();
            }}
            className={`px-3 py-1.5 text-xs rounded-md border ${
              scope === opt.id
                ? 'bg-sky-600 text-white border-sky-600'
                : 'border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {scope === 'segment' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Filter segments
            </label>
            <input
              type="text"
              value={segmentSearch}
              onChange={(e) => setSegmentSearch(e.target.value)}
              placeholder="Search…"
              className="w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
              Segment
            </label>
            <select
              value={segmentId === '' ? '' : String(segmentId)}
              onChange={(e) => {
                setSegmentId(e.target.value ? Number(e.target.value) : '');
                resetResults();
              }}
              className="w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-sm"
            >
              <option value="">Select segment…</option>
              {filteredSegments.map((s) => (
                <option key={s.segmentid} value={s.segmentid}>
                  {s.segmentname}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {scope === 'list' && (
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            List
          </label>
          <select
            value={listId === '' ? '' : String(listId)}
            onChange={(e) => {
              setListId(e.target.value ? Number(e.target.value) : '');
              resetResults();
            }}
            className="w-full max-w-md rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-sm"
          >
            <option value="">Select list…</option>
            {sortedLists.map((l) => (
              <option key={l.listid} value={l.listid}>
                {listLabel(l)}
              </option>
            ))}
          </select>
        </div>
      )}

      {scope === 'domain' && (
        <div className="max-w-md">
          <label className="block text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">
            Domain
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              value={domainInput}
              onChange={(e) => {
                setDomainInput(e.target.value);
                setDomainResult(null);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canRun && !running) void runScan(false);
              }}
              placeholder="example.com"
              className="w-full rounded-md border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-white text-sm pl-8"
            />
          </div>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
            Full multi-signal lookup (MX/SPF/DKIM/TXT/PTR) plus active contact count.
          </p>
        </div>
      )}

      {scope !== 'domain' && (
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-slate-200 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={excludeEdu}
            disabled={running}
            onChange={(e) => void applyExcludeEdu(e.target.checked)}
            className="rounded border-gray-300 dark:border-slate-600 text-sky-600 focus:ring-sky-500"
          />
          Exclude .edu domains
          <span className="text-xs text-gray-500 dark:text-slate-400">
            (counts and % of audience use this setting)
          </span>
        </label>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={running || !canRun}
          onClick={() => void runScan(false)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
          title={
            scope === 'domain'
              ? 'Resolve signals for this domain (uses cache if present)'
              : 'Look up domains missing multi-signal cache; skips already-enriched domains'
          }
        >
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          {scope === 'domain' ? 'Look up domain' : 'Run detection'}
        </button>
        <button
          type="button"
          disabled={running || !canRun}
          onClick={() => void runScan(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
          title="Re-run full multi-signal detection and overwrite cache"
        >
          <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {running && progress && scope !== 'domain' && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
            <span>
              Detecting filters… {progress.processed.toLocaleString()} / {progress.total.toLocaleString()}
              {excludeEdu ? ' (excl. .edu)' : ''}
            </span>
            <span>{pctComplete}%</span>
          </div>
          <div className="h-2 rounded-full bg-gray-100 dark:bg-slate-700 overflow-hidden">
            <div
              className="h-full bg-sky-500 transition-all duration-300"
              style={{ width: `${pctComplete}%` }}
            />
          </div>
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md px-3 py-2">
          {error}
        </div>
      )}

      {domainResult && scope === 'domain' && (
        <div className="rounded-md border border-sky-200 dark:border-sky-800/50 bg-sky-50/40 dark:bg-sky-900/10 p-4 space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              {domainResult.domain}
            </h4>
            <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
              {domainResult.contacts.toLocaleString()} active contacts ·{' '}
              {domainResult.confidence != null ? `${domainResult.confidence}% confidence · ` : ''}
              {domainResult.lookedUpAt
                ? `looked up ${new Date(domainResult.lookedUpAt).toLocaleString()}`
                : 'not looked up'}
            </p>
          </div>
          <dl className="grid gap-2 sm:grid-cols-2 text-sm">
            <div>
              <dt className="text-xs text-gray-500 dark:text-slate-400">Detected filter</dt>
              <dd className="font-medium text-gray-900 dark:text-white">{domainResult.filter}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-slate-400">SPF vendors</dt>
              <dd className="text-gray-700 dark:text-slate-200">
                {(domainResult.spfVendors || []).length ? domainResult.spfVendors!.join(', ') : '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500 dark:text-slate-400">MX hosts</dt>
              <dd className="text-gray-700 dark:text-slate-200 break-all">
                {domainResult.mxHosts.length ? domainResult.mxHosts.join(', ') : '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500 dark:text-slate-400">PTR / rDNS</dt>
              <dd className="text-gray-700 dark:text-slate-200 break-all">
                {(domainResult.ptrHosts || []).length ? domainResult.ptrHosts!.join(', ') : '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500 dark:text-slate-400">Signals</dt>
              <dd className="text-gray-700 dark:text-slate-200">
                {(domainResult.signals || []).length
                  ? domainResult.signals!.map((s) => `${s.kind}→${s.vendor}`).join(' · ')
                  : '—'}
              </dd>
            </div>
          </dl>
        </div>
      )}

      {summary && scope !== 'domain' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-slate-300">
            <span className="font-medium text-gray-900 dark:text-white">{summary.scopeLabel}</span>
            {' · '}
            {summary.totalContacts.toLocaleString()} contacts
            {' · '}
            {summary.totalDomains.toLocaleString()} domains
            {' · '}
            {summary.scannedDomains.toLocaleString()} with multi-signal cache
            {excludeEdu ? ' · excl. .edu' : ''}
          </p>
          <p className="text-xs text-gray-500 dark:text-slate-400">
            Tip: prior MX-only cache is upgraded automatically on Run (schema v2).
          </p>

          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-slate-600">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-slate-700/50 text-left text-xs text-gray-500 dark:text-slate-400">
                <tr>
                  <th className="px-3 py-2 font-medium">Filter / provider</th>
                  <th className="px-3 py-2 font-medium text-right">Domains</th>
                  <th className="px-3 py-2 font-medium text-right">Contacts</th>
                  <th className="px-3 py-2 font-medium text-right">% of audience</th>
                  <th className="px-3 py-2 font-medium text-right">Avg confidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                {summary.filters.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                      No detection data yet — run detection.
                    </td>
                  </tr>
                ) : (
                  summary.filters.map((row) => (
                    <tr
                      key={row.filter}
                      className="hover:bg-sky-50/50 dark:hover:bg-slate-700/40 cursor-pointer"
                      onClick={() => void openFilter(row.filter)}
                    >
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="font-medium text-sky-700 dark:text-sky-400 hover:underline text-left"
                        >
                          {row.filter}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {row.domains.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {row.contacts.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {row.pct.toFixed(1)}%
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-slate-300">
                        {row.avgConfidence != null ? `${row.avgConfidence}%` : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedFilter && scope !== 'domain' && (
        <div className="rounded-md border border-sky-200 dark:border-sky-800/50 bg-sky-50/40 dark:bg-sky-900/10 p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {selectedFilter}
              </h4>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5">
                {detailTotal.toLocaleString()} domains
                {excludeEdu ? ' (excl. .edu)' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={exporting || detailTotal === 0}
                onClick={() => void exportFilter()}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-700 disabled:opacity-50"
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedFilter(null);
                  setDetailRows([]);
                }}
                className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {detailLoading ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 py-4">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading domains…
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-slate-700/50 text-left text-gray-500 dark:text-slate-400">
                    <tr>
                      <th className="px-3 py-2 font-medium">Domain</th>
                      <th className="px-3 py-2 font-medium text-right">Contacts</th>
                      <th className="px-3 py-2 font-medium text-right">Conf.</th>
                      <th className="px-3 py-2 font-medium">Signals</th>
                      <th className="px-3 py-2 font-medium">MX hosts</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                    {detailRows.map((r) => (
                      <tr key={r.domain}>
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-white">
                          {r.domain}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r.contacts.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {r.confidence != null ? `${r.confidence}%` : '—'}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-600 dark:text-slate-300 max-w-xs truncate"
                          title={(r.signals || []).map((s) => `${s.kind}:${s.vendor} (${s.detail})`).join(' | ')}
                        >
                          {(r.signals || []).length
                            ? (r.signals || []).map((s) => s.kind).join(', ')
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-gray-600 dark:text-slate-300 max-w-md truncate" title={r.mxHosts.join(', ')}>
                          {r.mxHosts.length ? r.mxHosts.join(', ') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  Page {detailPage} of {detailPages}
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    disabled={detailPage <= 1 || detailLoading}
                    onClick={() => void loadFilterPage(selectedFilter, detailPage - 1)}
                    className="inline-flex items-center px-2 py-1 rounded border border-gray-200 dark:border-slate-600 disabled:opacity-40"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={detailPage >= detailPages || detailLoading}
                    onClick={() => void loadFilterPage(selectedFilter, detailPage + 1)}
                    className="inline-flex items-center px-2 py-1 rounded border border-gray-200 dark:border-slate-600 disabled:opacity-40"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
